
import { supabase } from '@/shared/api';
import { SupportedLanguage, SUPPORTED_LANGUAGES } from '@/shared/i18n';
import i18next from 'i18next';

let syncTimeout: ReturnType<typeof setTimeout> | null = null;
let suppressNextProfileSync = false;

/**
 * Syncs language changes to the user's profile in Supabase
 * Debounced to avoid excessive API calls
 */
async function syncLanguageToProfile(language: SupportedLanguage): Promise<void> {
  const DEBUG = import.meta.env.DEV;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (DEBUG) {
      console.log('[AuthLangSync] 🔍 Syncing language to profile', {
        userId: user.id,
        language,
        timestamp: new Date().toISOString()
      });
    }

    const { error } = await supabase
      .from('users')
      .update({ language: language })
      .eq('id', user.id);

    if (error) {
      if (DEBUG) {
        console.log('[AuthLangSync] 🔍 Language update error', {
          error: {
            message: error.message,
            code: error.code,
            details: error.details
          },
          userId: user.id,
          language,
          timestamp: new Date().toISOString()
        });
      }

      // Handle specific error cases
      if (error.code === 'PGRST116' || error.message?.includes('no rows returned')) {
        // User profile doesn't exist yet - this is normal for new users
        if (DEBUG) {
          console.log('[AuthLangSync] 🟡 Cannot sync language - user profile not created yet', {
            userId: user.id,
            language,
            timestamp: new Date().toISOString()
          });
        }
        // Save in localStorage as fallback for now
        localStorage.setItem('preferred_language', language);
      } else if (error.code === '42501' || error.message?.includes('permission denied')) {
        // RLS policy denied access
        if (DEBUG) {
          console.log('[AuthLangSync] 🟡 Cannot sync language - permission denied (user needs practice association)', {
            userId: user.id,
            language,
            timestamp: new Date().toISOString()
          });
        }
        // Save in localStorage as fallback
        localStorage.setItem('preferred_language', language);
      } else {
        // Other errors
        console.error('Failed to update preferred language in profile:', error);
      }
    } else {
      if (DEBUG) {
        console.log('[AuthLangSync] ✅ Language synced to profile successfully', {
          userId: user.id,
          language,
          timestamp: new Date().toISOString()
        });
      }
    }
  } catch (error) {
    if (DEBUG) {
      console.error('[AuthLangSync] 🔴 Error syncing language to profile', {
        error,
        language,
        timestamp: new Date().toISOString()
      });
    } else {
      console.error('Error syncing language to profile:', error);
    }
  }
}

/**
 * Debounced version of syncLanguageToProfile
 */
function debouncedSyncLanguageToProfile(language: SupportedLanguage): void {
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }
  
  syncTimeout = setTimeout(() => {
    syncLanguageToProfile(language);
  }, 500);
}

/**
 * Sets up authentication-based language synchronization
 * Call this once during app initialization
 */
export function setupAuthLanguageSync(): void {
  // Listen to authentication state changes
  supabase.auth.onAuthStateChange((event, session) => {
    const DEBUG = import.meta.env.DEV;
    if (DEBUG) {
       
      console.log('[AuthLangSync] auth event', {
        event,
        hasSession: !!session,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'n/a',
        online: typeof navigator !== 'undefined' ? navigator.onLine : 'n/a',
      });
    }

    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      // Defer to avoid blocking Supabase callbacks
      setTimeout(async () => {
        const user = session?.user;
        if (!user) return;

        const DEBUG = import.meta.env.DEV;

        // Check if user has practice association in their JWT claims
        const hasPracticeId = !!user.app_metadata?.practice_id;


        if (!hasPracticeId) {
          // User is new (signup flow) and doesn't have practice association yet
          // Skip database query and use browser language
          if (DEBUG) {
            console.log('[AuthLangSync] 🟡 User has no practice association - using browser language for signup flow', {
              userId: user.id,
              currentLanguage: i18next.language,
              timestamp: new Date().toISOString()
            });
          }

          // Use browser language as fallback for new users
          const browserLang = navigator.language?.split('-')[0] as SupportedLanguage;
          if (SUPPORTED_LANGUAGES.includes(browserLang) && i18next.language !== browserLang) {
            if (DEBUG) {
              console.log('[AuthLangSync] ✅ Applying browser language for new user', {
                from: i18next.language,
                to: browserLang,
                timestamp: new Date().toISOString()
              });
            }
            suppressNextProfileSync = true;
            await i18next.changeLanguage(browserLang);
            localStorage.setItem('preferred_language', browserLang);
          }
          return; // Skip database query entirely
        }

        // User has practice association - safe to query their language preference
        try {

          // Fetch user's preferred language from profile
          const { data, error } = await supabase
            .from('users')
            .select('language')
            .eq('id', user.id)
            .single();

          if (DEBUG) {
            console.log('[AuthLangSync] 🔍 Language query response', {
              hasData: !!data,
              language: data?.language,
              error: error ? {
                message: error.message,
                code: error.code,
                details: error.details
              } : null,
              timestamp: new Date().toISOString()
            });
          }

          if (!error && data?.language) {
            const preferredLang = data.language as SupportedLanguage;
            if (SUPPORTED_LANGUAGES.includes(preferredLang)) {
              if (i18next.language !== preferredLang) {
                if (DEBUG) {
                  console.log('[AuthLangSync] ✅ Applying user preferred language from database', {
                    from: i18next.language,
                    to: preferredLang,
                    timestamp: new Date().toISOString()
                  });
                }
                // Apply profile language without writing it back immediately
                suppressNextProfileSync = true;
                await i18next.changeLanguage(preferredLang);
                localStorage.setItem('preferred_language', preferredLang);
              }
            }
          } else if (error) {
            // Handle errors when user has practice but profile query fails
            if (DEBUG) {
              console.error('[AuthLangSync] 🔴 Failed to fetch language for user with practice association', {
                error,
                userId: user.id,
                practiceId: user.app_metadata.practice_id,
                timestamp: new Date().toISOString()
              });
            }
            // Use browser language as fallback
            const browserLang = navigator.language?.split('-')[0] as SupportedLanguage;
            if (SUPPORTED_LANGUAGES.includes(browserLang) && i18next.language !== browserLang) {
              suppressNextProfileSync = true;
              await i18next.changeLanguage(browserLang);
              localStorage.setItem('preferred_language', browserLang);
            }
          }
        } catch (error) {
          // Network or other unexpected errors
          if (DEBUG) {
            console.error('[AuthLangSync] 🔴 Unexpected error fetching user language', {
              error,
              userId: user.id,
              timestamp: new Date().toISOString()
            });
          }
        }
      }, 0);
    }
  });

  // Listen to language changes and sync to profile
  i18next.on('languageChanged', (language) => {
    if (SUPPORTED_LANGUAGES.includes(language as SupportedLanguage)) {
      if (suppressNextProfileSync) {
        suppressNextProfileSync = false;
        return;
      }
      debouncedSyncLanguageToProfile(language as SupportedLanguage);
    }
  });
}
